import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { IconId, IconTruckDelivery } from '@tabler/icons-react';
import { Ci2EditPopover } from '../../process-config/components/Ci2EditPopover';
import {
  CODE_MAX_LEN,
  codesPolicyForTipo,
  filterAvailableGoCodes,
  goCodeInUseMessage,
  goCodeKey,
  isValidGoCode,
  sanitizeGoCode,
} from '../utils/goUserCodeRules';
import '../../process-config/components/CondicoesIntervencaoTableV2.css';

const FIELD_META = {
  codtech: {
    title: 'Código do técnico',
    subtitle: 'Código do técnico no ERP ou do técnico nativo, G### (campo codtech no App Go).',
    tooltip: 'Código do técnico no ERP',
    emptyTooltip: 'Definir código do técnico',
    pillClass: 'go-usr-codtec-pill',
    emptyClass: 'go-usr-cod-empty go-usr-cod-empty--tec',
    EmptyIcon: IconId,
  },
  codmoto: {
    title: 'Código do motorista',
    subtitle: 'Código do motorista no pedido (campo codmoto no App Go).',
    tooltip: 'Código do motorista',
    emptyTooltip: 'Definir código de motorista',
    pillClass: 'go-usr-codmoto-pill',
    emptyClass: 'go-usr-cod-empty go-usr-cod-empty--moto',
    EmptyIcon: IconTruckDelivery,
  },
};

function catalogForField(catalog, field) {
  if (Array.isArray(catalog)) return catalog;
  if (!catalog || typeof catalog !== 'object') return [];
  const list = field === 'codmoto' ? catalog.moto : catalog.tech;
  return Array.isArray(list) ? list : [];
}

export function GoUsrCodePicker({
  field,
  value,
  onChange,
  catalog,
  usedMap,
  excludeIdUser,
  disabled,
  inputId,
  inputRef,
  onEnter,
  listOnSearchOnly = false,
  includeOccupied = false,
}) {
  const meta = FIELD_META[field] || FIELD_META.codtech;
  const [query, setQuery] = useState(value || '');
  const [searchActive, setSearchActive] = useState(false);
  useEffect(() => {
    setQuery(value || '');
    setSearchActive(false);
  }, [value]);

  const fieldCatalog = useMemo(() => catalogForField(catalog, field), [catalog, field]);
  const options = useMemo(
    () =>
      includeOccupied
        ? fieldCatalog
        : filterAvailableGoCodes(fieldCatalog, usedMap, excludeIdUser),
    [fieldCatalog, usedMap, excludeIdUser, includeOccupied]
  );
  const showList = !listOnSearchOnly || searchActive;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (listOnSearchOnly && !searchActive) return [];
    if (!q) return options;
    return options.filter(
      (item) => item.code.toLowerCase().includes(q) || String(item.name || '').toLowerCase().includes(q)
    );
  }, [options, query, listOnSearchOnly, searchActive]);

  const selectedKey = goCodeKey(value);
  const typedCode = sanitizeGoCode(query);
  const lookupKey = selectedKey || goCodeKey(typedCode);
  const owner = lookupKey && usedMap?.[lookupKey];
  const conflict =
    owner && Number(owner.idUser) !== Number(excludeIdUser)
      ? goCodeInUseMessage(field, lookupKey, owner.username)
      : null;

  const applyQuery = (raw) => {
    setQuery(raw);
    if (listOnSearchOnly) setSearchActive(raw.trim().length > 0);
    const digits = sanitizeGoCode(raw);
    if (digits.length === CODE_MAX_LEN || raw === '') onChange(digits);
  };

  return (
    <div className={`go-usr-code-picker${listOnSearchOnly ? ' go-usr-code-picker--search-first' : ''}`}>
      <input
        id={inputId}
        ref={inputRef}
        type="search"
        className="ci2-pop-input ci2-grupo-row-input go-usr-enable-code-input"
        value={query}
        onChange={(e) => applyQuery(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        placeholder="Buscar nome ou código"
        disabled={disabled}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !conflict && onEnter) {
            e.preventDefault();
            onEnter();
          }
        }}
      />
      {showList ? (
        <div className="go-usr-code-picker-list" role="listbox" aria-label={meta.title}>
          {filtered.map((item) => {
            const selected = item.code === selectedKey;
            const itemOwner = usedMap?.[item.code];
            const taken =
              Boolean(itemOwner) &&
              Number(itemOwner.idUser) !== Number(excludeIdUser);
            return (
              <button
                key={item.code}
                type="button"
                role="option"
                aria-selected={selected}
                className={`go-usr-code-picker-item${selected ? ' is-selected' : ''}${
                  taken ? ' is-taken' : ''
                }`}
                disabled={disabled || taken}
                onClick={() => {
                  setQuery(item.code);
                  onChange(item.code);
                }}
              >
                <span className={meta.pillClass}>{item.code}</span>
                <span className="go-usr-code-picker-name">{item.name}</span>
                {taken ? (
                  <span className="go-usr-code-picker-taken">
                    Em uso{itemOwner.username ? ` · ${itemOwner.username}` : ''}
                  </span>
                ) : null}
              </button>
            );
          })}
          {filtered.length === 0 ? (
            <div className="go-usr-code-picker-empty">Nenhum código disponível</div>
          ) : null}
        </div>
      ) : null}
      {conflict ? <p className="go-usr-code-picker-error">{conflict}</p> : null}
    </div>
  );
}

export function GoUsrCodeEditPopover({
  open,
  anchorRef,
  field,
  initialValue,
  saving,
  required = true,
  catalog,
  usedMap,
  excludeIdUser,
  onClose,
  onSave,
}) {
  const meta = FIELD_META[field] || FIELD_META.codtech;
  const inputRef = useRef(null);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (open) setDraft(sanitizeGoCode(initialValue));
  }, [open, initialValue]);

  const selectedKey = goCodeKey(draft);
  const owner = selectedKey && usedMap?.[selectedKey];
  const conflict =
    owner && Number(owner.idUser) !== Number(excludeIdUser)
      ? goCodeInUseMessage(field, draft, owner.username)
      : null;
  const lengthOk = required
    ? draft.length === CODE_MAX_LEN
    : draft.length === 0 || draft.length === CODE_MAX_LEN;
  const canSave = lengthOk && !conflict;
  const inputId = `go-usr-code-${field}`;

  return (
    <Ci2EditPopover
      open={open}
      anchorRef={anchorRef}
      onClose={onClose}
      width={280}
      maxHeight={380}
      estimatedHeight={300}
      title={meta.title}
      className="ci2-edit-popover--grupo ci2-edit-popover--single"
      initialFocusRef={inputRef}
    >
      <div className="ci2-edit-popover-head ci2-edit-popover-head--shadcn">
        <h4 className="ci2-edit-popover-title">{meta.title}</h4>
        <p className="ci2-edit-popover-sub">{meta.subtitle}</p>
      </div>
      <div className="ci2-edit-popover-body ci2-grupo-rows">
        <GoUsrCodePicker
          field={field}
          value={draft}
          onChange={setDraft}
          catalog={catalog}
          usedMap={usedMap}
          excludeIdUser={excludeIdUser}
          disabled={saving}
          inputId={inputId}
          inputRef={inputRef}
          onEnter={() => {
            if (canSave && !saving) onSave(draft);
          }}
        />
      </div>
      <div className="ci2-edit-popover-foot">
        <button type="button" className="ci2-pop-btn ci2-pop-btn-ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </button>
        <button
          type="button"
          className="ci2-pop-btn ci2-pop-btn-primary"
          onClick={() => onSave(draft)}
          disabled={!canSave || saving}
          title={conflict || (canSave ? 'Salvar alteração' : 'Informe exatamente 4 dígitos')}
        >
          {saving ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : null}
          {saving ? 'Salvando...' : 'Confirmar'}
        </button>
      </div>
    </Ci2EditPopover>
  );
}

/**
 * Pills de código (técnico / motorista) com tooltip estilo ci2 e popover de edição.
 */
export function GoUsrCodePills({
  row,
  disabled,
  codeEdit,
  onOpenCodeEdit,
  onCloseCodeEdit,
  onSaveCode,
  catalog,
  usedTech,
  usedMoto,
}) {
  const tecRef = useRef(null);
  const motoRef = useRef(null);
  const pending = row.enabled === false;
  const policy = codesPolicyForTipo(row.tipoUser);
  const showTec = !pending && policy.allowCodtech;
  const showMoto = !pending && policy.allowCodmoto;
  const codtec = String(row.codtec || '').trim();
  const codmoto = String(row.codmoto || '').trim();

  const tecOpen = codeEdit?.idUser === row.idUser && codeEdit?.field === 'codtech';
  const motoOpen = codeEdit?.idUser === row.idUser && codeEdit?.field === 'codmoto';
  const saving = Boolean(codeEdit?.saving && codeEdit?.idUser === row.idUser);

  return (
    <>
      {pending && codtec ? <span className="go-usr-codtec-pill">{codtec}</span> : null}

      {showTec ? (
        <>
          <button
            ref={tecRef}
            type="button"
            className={`go-usr-code-trigger go-usr-modern-tooltip-host${
              !codtec ? ` ${FIELD_META.codtech.emptyClass}` : ''
            }`}
            data-tooltip={codtec ? FIELD_META.codtech.tooltip : FIELD_META.codtech.emptyTooltip}
            disabled={disabled}
            aria-label={codtec ? FIELD_META.codtech.tooltip : FIELD_META.codtech.emptyTooltip}
            onClick={(e) => {
              e.stopPropagation();
              onOpenCodeEdit(row.idUser, 'codtech');
            }}
          >
            {codtec ? (
              <span className={FIELD_META.codtech.pillClass}>{codtec}</span>
            ) : (
              <span className="go-usr-cod-empty-icon" aria-hidden>
                <IconId size={13} stroke={1.75} />
              </span>
            )}
          </button>
          <GoUsrCodeEditPopover
            open={tecOpen}
            anchorRef={tecRef}
            field="codtech"
            initialValue={codtec}
            saving={saving && tecOpen}
            required={policy.needCodtech}
            catalog={catalog}
            usedMap={usedTech}
            excludeIdUser={row.idUser}
            onClose={onCloseCodeEdit}
            onSave={(value) => onSaveCode(row.idUser, 'codtech', value)}
          />
        </>
      ) : null}

      {showMoto ? (
        <>
          <button
            ref={motoRef}
            type="button"
            className={`go-usr-code-trigger go-usr-modern-tooltip-host${
              !codmoto ? ` ${FIELD_META.codmoto.emptyClass}` : ''
            }`}
            data-tooltip={
              codmoto ? FIELD_META.codmoto.tooltip : FIELD_META.codmoto.emptyTooltip
            }
            disabled={disabled}
            aria-label={
              codmoto ? FIELD_META.codmoto.tooltip : FIELD_META.codmoto.emptyTooltip
            }
            onClick={(e) => {
              e.stopPropagation();
              onOpenCodeEdit(row.idUser, 'codmoto');
            }}
          >
            {codmoto ? (
              <span className={FIELD_META.codmoto.pillClass}>{codmoto}</span>
            ) : (
              <span className="go-usr-cod-empty-icon" aria-hidden>
                <IconTruckDelivery size={13} stroke={1.75} />
              </span>
            )}
          </button>
          <GoUsrCodeEditPopover
            open={motoOpen}
            anchorRef={motoRef}
            field="codmoto"
            initialValue={codmoto}
            saving={saving && motoOpen}
            required={policy.needCodmoto}
            catalog={catalog}
            usedMap={usedMoto}
            excludeIdUser={row.idUser}
            onClose={onCloseCodeEdit}
            onSave={(value) => onSaveCode(row.idUser, 'codmoto', value)}
          />
        </>
      ) : null}
    </>
  );
}

export { isValidGoCode, sanitizeGoCode, codesPolicyForTipo, CODE_MAX_LEN };
